import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    Avatar, Box, Button, Chip, CircularProgress, GlobalStyles, Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getPublicData, serverPath } from '../../services/ajax';
import { buildVerificacionUrl } from '../../utils/credencialUrl';
import { APP_ORGANISM, APP_SHORT_NAME } from '../../const/app';
import QrImage from '../../components/qr/QrImage';
import logo from '../../images/logo_negro_color.png';

const INSTITUCION = {
    azul: '#00203a',
    azulClaro: '#013a5e',
    verde: '#2e7d32',
    verdeClaro: '#e8f5e9',
    acento: '#8a9a46',
};

function Page() {
    const { codigoPublico, token } = useParams();
    const [searchParams] = useSearchParams();
    const [estado, setEstado] = useState('LOADING');
    const [datos, setDatos] = useState(null);

    const verificar = useCallback(async () => {
        setEstado('LOADING');
        const resp = await getPublicData(`public/credenciales/${codigoPublico}/${token}`);
        if (resp.error) {
            setEstado('ERROR_CONEXION');
            setDatos(null);
            return;
        }
        const d = resp.data || {};
        setDatos(d);
        setEstado(d.estado_credencial || 'INVALIDA');
    }, [codigoPublico, token]);

    useEffect(() => { verificar(); }, [verificar]);

    const esValida = estado === 'VALIDA';
    const qrUrl = buildVerificacionUrl(codigoPublico, token);
    const fotoSrc = datos?.foto_url
        ? `${serverPath()}public/credenciales/${codigoPublico}/${token}/foto`
        : null;

    useEffect(() => {
        if (searchParams.get('autoprint') !== '1' || !esValida || !datos) return undefined;
        const t = setTimeout(() => window.print(), 900);
        return () => clearTimeout(t);
    }, [searchParams, esValida, datos]);

    return (
        <>
            <GlobalStyles styles={{
                '@media print': {
                    'html, body': { margin: 0, padding: 0, background: '#fff !important' },
                    '#root': { background: '#fff !important' },
                    '.no-print': { display: 'none !important' },
                    '.credencial-print-page': {
                        minHeight: 'auto !important',
                        background: '#fff !important',
                        padding: '0 !important',
                    },
                    '.credencial-card': {
                        boxShadow: 'none !important',
                        border: '0.4mm solid #ccc',
                        breakInside: 'avoid',
                        pageBreakInside: 'avoid',
                    },
                },
                '@page': {
                    size: 'auto',
                    margin: '12mm',
                },
            }} />

            <Box className="credencial-print-page" sx={{
                minHeight: '100vh',
                background: `linear-gradient(160deg, ${INSTITUCION.azul} 0%, ${INSTITUCION.azulClaro} 100%)`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: { xs: 2, sm: 4 },
                px: 2,
            }}>
                <Box className="no-print" sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={() => window.print()}
                        disabled={!esValida}
                        sx={{ bgcolor: INSTITUCION.acento, '&:hover': { bgcolor: '#75863c' } }}
                    >
                        Imprimir credencial
                    </Button>
                    <Button variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.6)' }} onClick={() => window.close()}>
                        Cerrar
                    </Button>
                </Box>

                {estado === 'LOADING' && (
                    <Box sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress sx={{ color: '#fff' }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>Cargando credencial…</Typography>
                    </Box>
                )}

                {estado === 'ERROR_CONEXION' && (
                    <Box className="no-print" sx={{ textAlign: 'center', color: '#fff' }}>
                        <Typography variant="h6" gutterBottom>No se pudo cargar la credencial</Typography>
                        <Button variant="contained" onClick={verificar}>Reintentar</Button>
                    </Box>
                )}

                {estado !== 'LOADING' && estado !== 'ERROR_CONEXION' && !esValida && (
                    <Box sx={{
                        bgcolor: '#fff', borderRadius: 2, p: 4, maxWidth: 420, textAlign: 'center',
                    }}>
                        <WarningAmberIcon sx={{ fontSize: 48, color: '#ed6c02', mb: 1 }} />
                        <Typography variant="h6" gutterBottom>No se puede imprimir</Typography>
                        <Typography variant="body2" color="text.secondary">
                            La credencial no está vigente ({estado.replace(/_/g, ' ').toLowerCase()}).
                            Solo se imprimen credenciales válidas y activas.
                        </Typography>
                    </Box>
                )}

                {esValida && datos && (
                    <Box
                        className="credencial-card"
                        sx={{
                            width: '100%',
                            maxWidth: 420,
                            bgcolor: '#fff',
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
                        }}
                    >
                        {/* Encabezado institucional */}
                        <Box sx={{
                            bgcolor: INSTITUCION.azul,
                            color: '#fff',
                            px: 2.5,
                            py: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                        }}>
                            <img src={logo} alt="Ministerio de Salud Chaco" style={{ height: 44, flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" sx={{ opacity: 0.85, letterSpacing: 0.6, textTransform: 'uppercase' }}>
                                    {APP_ORGANISM}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    Credencial de personal
                                </Typography>
                            </Box>
                        </Box>

                        {/* Datos del titular */}
                        <Box sx={{ px: 2.5, py: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                <Avatar
                                    src={fotoSrc || undefined}
                                    sx={{ width: 88, height: 88, bgcolor: INSTITUCION.azul, border: `2px solid ${INSTITUCION.acento}` }}
                                >
                                    {!fotoSrc && (datos.nombre_apellido ? datos.nombre_apellido.charAt(0) : '?')}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25, mb: 0.75 }}>
                                        {datos.nombre_apellido || '-'}
                                    </Typography>
                                    {datos.texto_estado_personal && (
                                        <Chip
                                            icon={<VerifiedUserIcon />}
                                            label={datos.texto_estado_personal}
                                            size="small"
                                            sx={{
                                                bgcolor: INSTITUCION.verdeClaro,
                                                color: INSTITUCION.verde,
                                                fontWeight: 600,
                                                '& .MuiChip-icon': { color: INSTITUCION.verde },
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>

                            <Campo label="Área de desempeño" value={datos.area_desempeno || 'No informada'} />
                            <Campo label="CUOF del área" value={datos.cuof || 'No informado'} />
                            <Campo label="Función actual" value={datos.funcion_actual || 'No informada'} />
                        </Box>

                        {/* QR de verificación */}
                        <Box sx={{
                            bgcolor: '#f8fafb',
                            borderTop: '1px solid #e8edf0',
                            px: 2.5,
                            py: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                        }}>
                            <QrImage value={qrUrl} size={180} alt="Código QR de verificación" />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: INSTITUCION.azul }}>
                                Escanear para verificar
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                Credencial digital · {APP_SHORT_NAME}
                            </Typography>
                        </Box>

                        {/* Pie institucional */}
                        <Box sx={{
                            bgcolor: INSTITUCION.acento,
                            color: '#fff',
                            px: 2.5,
                            py: 1.25,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                        }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
                                {APP_SHORT_NAME}
                            </Typography>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', opacity: 0.9, fontSize: 10 }}>
                                {codigoPublico}
                            </Typography>
                        </Box>
                    </Box>
                )}

                <Typography className="no-print" variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', mt: 2, textAlign: 'center' }}>
                    Vista previa de impresión
                </Typography>
            </Box>
        </>
    );
}

function Campo({ label, value }) {
    return (
        <Box sx={{ mb: 1.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>{value}</Typography>
        </Box>
    );
}

export default Page;
